import { cn } from '@commenda/utils';

import { Icons } from '../../Icons';
import { Tooltip } from '../../Tooltip/Tooltip';
import { TableSummaryConfig } from '../types';

interface TableSummaryProps {
  config: TableSummaryConfig;
}

export function TableSummary({ config }: TableSummaryProps) {
  const { items, className, variant = 'inline' } = config;

  if (!items.length) return null;

  const isCards = variant === 'cards';

  return (
    <div
      className={cn(
        isCards
          ? 'flex items-stretch gap-xl pb-xl'
          : 'flex items-start gap-4xl border-y border-border-secondary p-xl',
        className
      )}
    >
      {items.map((item) => {
        const content = isCards ? (
          <>
            <span className="flex items-center gap-xs txt-small-medium text-text-tertiary">
              {item.label}
              {item.tooltip ? (
                <Tooltip content={item.tooltip} asChild>
                  <span tabIndex={0} aria-label={`About ${item.label}`}>
                    <Icons.InfoCircle className="size-3" />
                  </span>
                </Tooltip>
              ) : null}
            </span>
            <span className="txt-large-medium text-text-primary">
              {item.value}
            </span>
          </>
        ) : (
          <>
            <span className="flex items-center gap-xs txt-extra-small-regular text-text-tertiary">
              {item.label}
              {item.tooltip ? (
                <Tooltip content={item.tooltip} asChild>
                  <span tabIndex={0} aria-label={`About ${item.label}`}>
                    <Icons.InfoCircle className="size-3" />
                  </span>
                </Tooltip>
              ) : null}
            </span>
            <span className="txt-extra-large-medium text-text-primary">
              {item.value}
            </span>
          </>
        );

        const itemClassName = isCards
          ? cn(
              'flex flex-1 items-center justify-between rounded-md border bg-bg-primary px-xl py-2xl text-left',
              item.filter?.isActive
                ? 'border-fg-brand ring-4 ring-bg-brand-hover'
                : 'border-border-secondary'
            )
          : cn(
              'flex flex-col gap-xs',
              item.filter &&
                cn(
                  'rounded-md px-lg py-sm text-left transition-colors',
                  item.filter.isActive
                    ? 'bg-bg-active-dark'
                    : 'hover:bg-bg-secondary'
                )
            );

        return item.filter ? (
          <button
            key={item.label}
            type="button"
            aria-pressed={item.filter.isActive}
            onClick={item.filter.onClick}
            className={itemClassName}
          >
            {content}
          </button>
        ) : (
          <div key={item.label} className={itemClassName}>
            {content}
          </div>
        );
      })}
    </div>
  );
}
